import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatINR, fmtDate } from "@/lib/roles";
import { Loader2, ArrowLeft, MapPin, Home, User, Wallet, Receipt } from "lucide-react";

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-2 py-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 dark:text-slate-100 font-medium">{value || "—"}</div>
    </div>
  );
}

export default function PropertyDetail() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/properties/${pid}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [pid]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return <div className="text-center py-10">Not found.</div>;
  const { property: p, demands, payments } = data;

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/properties"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Property</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 dark:text-white">
          {p.property_id}
          <Badge variant="outline" className="ml-3 capitalize">{p.property_type}</Badge>
        </h1>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.address}, {p.ward}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="property-info-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Home className="w-4 h-4" /> Property Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Row label="Ward / Zone" value={`${p.ward} / ${p.zone || "—"}`} />
            <Row label="Municipal Body" value={p.municipal_body} />
            <Row label="Plot Number" value={p.plot_number} />
            <Row label="Built-up Area" value={`${p.built_up_area} sq.ft`} />
            <Row label="Plot Area" value={p.plot_area ? `${p.plot_area} sq.ft` : "—"} />
            <Row label="Floors" value={p.number_of_floors} />
            <Row label="Year Built" value={p.year_of_construction} />
            <Row label="Usage" value={<span className="capitalize">{p.usage_type?.replace("_", " ")}</span>} />
            <Row label="Latitude" value={p.latitude?.toFixed(5)} />
            <Row label="Longitude" value={p.longitude?.toFixed(5)} />
          </CardContent>
        </Card>

        <Card data-testid="owner-info-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><User className="w-4 h-4" /> Owner Details</CardTitle></CardHeader>
          <CardContent>
            <Row label="Name" value={p.owner_name} />
            <Row label="Email" value={p.owner_email} />
            <Row label="Phone" value={p.owner_phone} />
            <Row label="ID (masked)" value={p.owner_aadhaar} />
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Annual Tax</div>
              <div className="text-2xl font-heading font-bold tabular-nums text-[#0F2942] dark:text-white">{formatINR(p.annual_tax)}</div>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total Dues</div>
              <div className={`text-2xl font-heading font-bold tabular-nums ${p.total_dues > 0 ? "text-red-600" : "text-[#0D7650]"}`}>{formatINR(p.total_dues)}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Demands */}
      <Card data-testid="demands-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Wallet className="w-4 h-4" /> Demand Notices</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-2 px-4">Demand ID</th><th className="text-left py-2 px-4">Period</th><th className="text-right py-2 px-4">Total</th><th className="text-right py-2 px-4">Paid</th><th className="text-left py-2 px-4">Due</th><th></th></tr>
              </thead>
              <tbody>
                {demands.map((d) => (
                  <tr key={d.id} className="border-t border-slate-200 dark:border-slate-800">
                    <td className="py-2 px-4 font-mono text-xs">{d.demand_id}</td>
                    <td className="py-2 px-4">{d.period}</td>
                    <td className="py-2 px-4 text-right font-mono">{formatINR(d.total)}</td>
                    <td className="py-2 px-4 text-right font-mono">{formatINR(d.paid)}</td>
                    <td className="py-2 px-4">{fmtDate(d.due_date)}</td>
                    <td className="py-2 px-4 text-right">
                      <Badge variant={d.status === "paid" ? "default" : d.status === "overdue" ? "destructive" : "secondary"} className="capitalize">{d.status}</Badge>
                      {d.status !== "paid" && (
                        <Link to={`/portal/pay/${d.id}`}>
                          <Button size="sm" variant="ghost" className="ml-2" data-testid={`pay-${d.id}`}>Pay</Button>
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
                {demands.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">No demands yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payment history */}
      <Card data-testid="payment-history-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Receipt className="w-4 h-4" /> Payment History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-2 px-4">Receipt</th><th className="text-left py-2 px-4">Date</th><th className="text-left py-2 px-4">Method</th><th className="text-right py-2 px-4">Amount</th><th></th></tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-200 dark:border-slate-800">
                    <td className="py-2 px-4 font-mono text-xs">{p.receipt_number}</td>
                    <td className="py-2 px-4">{fmtDate(p.completed_at)}</td>
                    <td className="py-2 px-4 uppercase text-xs">{p.method}</td>
                    <td className="py-2 px-4 text-right font-mono">{formatINR(p.amount)}</td>
                    <td className="py-2 px-4 text-right">
                      <Link to={`/receipts/${p.receipt_number || p.payment_id}`}><Button size="sm" variant="ghost">View</Button></Link>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">No payments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
